import React, { useEffect, useMemo, useRef, useState } from 'react';

import useProcedureStore from '../../../stores/procedureStore';

const labelCls = 'block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1';
const inputCls = 'w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm';
const helperCls = 'text-[11px] text-slate-500 dark:text-slate-400 mt-1';
const errorCls = 'text-[11.5px] text-red-600 dark:text-red-400 mt-1';
const readonlyValueCls = 'text-[13px] text-slate-800 dark:text-slate-100';
const btnSecondary = 'px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm disabled:opacity-60';
const btnRemove = 'px-2 py-1.5 rounded-lg border border-red-300 dark:border-red-700 bg-white dark:bg-slate-800 text-red-700 dark:text-red-300 text-sm';

// D2/D4 (build-plan-phase1.md §R.1) — caps the FE mirrors as a HINT only; the
// server (BE-C1) is the enforcer. Exported so FE-C2's Save-gating mirror and
// any future consumer reuse the SAME numbers instead of a second literal.
export const INVESTIGATION_ROWS_MAX = 40;
export const INVESTIGATION_RESULT_MAX_LENGTH = 200;
// D1: the catalogue's `max_length` for the `investigations` key is 120 — that
// travels on the wire as `field.max_length` and is this component's default
// when the caller doesn't pass one explicitly.
export const INVESTIGATION_NAME_MAX_LENGTH_DEFAULT = 120;

// Follow-on DEF-INV-FE — shown under the rows ONLY when this mount seeded
// its rows from the organisation's default list (`prefill`), never from a
// stored value. Exported so `SummaryFields.jsx`/tests share the one string.
export const INVESTIGATION_PREFILL_HINT_COPY = "Pre-filled from your organisation's default list — only rows with a result are printed.";

const CATALOGUE_OPTIONS_CAP = 20;

// The exact filter `LabAdvisePanel.jsx` uses for its dept-scoped picker —
// reused verbatim (memory: prefer_minimal_repurpose). No `receptionist_allowed`
// filter here: this is a doctor surface (discharge summary), not the desk.
function isLabCatalogueRow(p) {
  return !!p && p.category === 'lab' && p.active !== false;
}

// D2's row shape, denormalised to the internal editing shape (`_id` added for
// stable React keys — a row's `name` is not a safe key, it can be edited to
// match another row, or be blank while the doctor is still typing it).
function toInternalRow(row, makeId) {
  const source = row && row.source === 'catalogue' ? 'catalogue' : 'custom';
  return {
    _id: makeId(),
    name: (row && row.name) || '',
    result: (row && row.result) || '',
    source,
    procedure_id: source === 'catalogue' ? ((row && row.procedure_id) || null) : null,
  };
}

// D2's normalisation, mirrored client-side as a HINT (the server still
// enforces it on save): trim name/result, `result: ''` -> null, and a row
// whose trimmed name is empty is DROPPED SILENTLY — that is what lets the
// doctor keep an in-progress blank row on screen (still rendered locally)
// without it ever reaching the parent's committed value. Empty list -> null,
// never `[]` (same "null = never set" semantic as every other catalogue key).
function toExternalValue(rows) {
  const external = rows
    .map((r) => ({
      name: (r.name || '').trim(),
      result: (r.result || '').trim() || null,
      source: r.source,
      procedure_id: r.source === 'catalogue' ? (r.procedure_id || null) : null,
    }))
    .filter((r) => r.name !== '');
  return external.length > 0 ? external : null;
}

/**
 * InvestigationRowsField (DISCHARGE-TEMPLATES Phase 1 round-1 rework · FE-C1,
 * NEW FILE ONLY — build-plan-phase1.md §R.1/§R.2) — the row editor for the
 * `investigations` key once it flips from a plain narrative textarea to
 * `kind: "lab_lines"` (D1). A controlled leaf component: `value` is the row
 * array (D2 shape) or `null`, `onChange(nextRows|null)` is the single way the
 * parent (SummaryFields, wired by FE-C2 — not this file) learns of a change.
 * `disabled` renders every row read-only; `label` is the field's caption;
 * `maxLength` is the per-row NAME cap the catalogue emits (120, D1) — the
 * RESULT cap (200) and the ROW cap (40) are NOT on the wire (D4) so they are
 * local constants exported above.
 *
 * INTERNAL vs EXTERNAL value — deliberate split, not an accident:
 *   - Internal `rows` state renders EVERY row the doctor has touched,
 *     including a freshly-added blank one (so "+ Add investigation" is not a
 *     no-op) and a row mid-edit with a blank name.
 *   - The value handed to `onChange` (`toExternalValue`) drops any row whose
 *     trimmed name is still blank — the same "blank rows dropped" rule the
 *     server enforces (D2), applied here only to what leaves the component,
 *     never to what is rendered.
 *   - The component re-seeds its internal rows from `value` only when `value`
 *     is a DIFFERENT reference than the one it last emitted itself (tracked
 *     in `lastEmittedRef`) — i.e. an external reset (a fresh admission
 *     loaded, or the hosting panel reverting unsaved edits), never its own
 *     echo. ASSUMPTION for FE-C2: the hosting panel must store whatever
 *     reference `onChange` hands it verbatim (not a fresh clone) for this to
 *     hold — the same assumption `SummaryFields`' other controlled inputs
 *     already make of their host.
 *
 * DEFAULT-LIST PREFILL (follow-on DEF-INV-FE) — `prefill` (the field's own
 * `field.prefill`, an organisation-default row array BE-side, DEF-INV-BE) is
 * consulted ONLY by the mount-time `rows` seed, exactly the header-field
 * `value ?? prefill` pattern: `value == null` (never saved) AND `prefill` is
 * a non-empty array -> seed from `prefill` instead of starting empty, so the
 * doctor sees the organisation's default tests with blank result boxes.
 * Deliberately NOT part of the re-seed effect below (which only reacts to a
 * genuine new `value` reference from the parent) — once the doctor removes
 * every default row, `commit([])` emits `null` and stores it in
 * `lastEmittedRef`; the NEXT render's `value` prop is that same `null`
 * (same reference the component itself just emitted, not a new external
 * one), so the re-seed effect's own-echo guard skips it and the defaults do
 * NOT reappear. `disabled` (locked/read-only) never seeds from `prefill` —
 * a closed summary prints only what was actually recorded.
 *
 * CATALOGUE — reuses `useProcedureStore` exactly as `LabAdvisePanel.jsx`
 * does: `fetchProceduresForDept(null)` (org-wide — "the organisation's lab
 * catalogue", no department scoping; the frozen prop contract here carries no
 * `admission`/department, so there is nothing to scope by) + `pickerProcedures`
 * filtered `category === 'lab' && active !== false`. ONE fetch per mount
 * (empty-deps effect calling a store action whose reference never changes,
 * and which is itself single-flight/idempotent per-anchor — no request
 * storm). A failed fetch never blocks or crashes the field: it degrades to
 * plain custom typing (the catalogue list is simply empty, so every combobox
 * shows only the "Use as custom" escape) plus one hint line.
 *
 * COMBOBOX — one row's dropdown open at a time (`openRowId`). Typing a name
 * ALWAYS sets `source:'custom', procedure_id:null` immediately (typing
 * invalidates any prior catalogue tie — the displayed name no longer matches
 * the picked item verbatim); picking a catalogue option overwrites
 * `name`/`source:'catalogue'`/`procedure_id`. "Use as custom: «typed»" is the
 * explicit escape row (D2/§R.2) — it is idempotent with the typing rule above
 * but gives the doctor an affirmative "use this text" action and closes the
 * dropdown. Matches are capped at 20 with a "+n more — keep typing" footer
 * (substring, case-insensitive).
 */
export default function InvestigationRowsField({
  value, onChange, disabled = false, label, maxLength = INVESTIGATION_NAME_MAX_LENGTH_DEFAULT, prefill = null,
  error = null, errorRow = null,
}) {
  const pickerProcedures = useProcedureStore((s) => s.pickerProcedures);
  const fetchProceduresForDept = useProcedureStore((s) => s.fetchProceduresForDept);
  const [catalogueStatus, setCatalogueStatus] = useState('loading'); // 'loading' | 'loaded' | 'error'

  const idRef = useRef(0);
  const makeId = () => {
    idRef.current += 1;
    return `inv-row-${idRef.current}`;
  };

  // Mount-only seed decision (see the JSDoc's "DEFAULT-LIST PREFILL" note) —
  // computed once and frozen for the life of this mount via `useRef`, never
  // re-evaluated on a later render (that is exactly what would let cleared
  // defaults reappear).
  const seededFromPrefillRef = useRef(!disabled && value == null && Array.isArray(prefill) && prefill.length > 0);
  const seededFromPrefill = seededFromPrefillRef.current;

  const [rows, setRows] = useState(() => {
    const seed = seededFromPrefillRef.current ? prefill : value;
    return (Array.isArray(seed) ? seed : []).map((r) => toInternalRow(r, makeId));
  });
  const lastEmittedRef = useRef(value ?? null);
  const [openRowId, setOpenRowId] = useState(null);
  // QA-verified fix (DISCHARGE-TEMPLATES Phase 1 round-1 defect, DEF-1 — see
  // the identical guard + comment in `BulletLinesField.jsx`): the mount
  // run of the re-seed effect below can be deferred by React past a
  // same-tick edit, reads a stale mount `value` against a `lastEmittedRef`
  // that has already advanced past the doctor's first edit, and wrongly
  // reseeds from the ORIGINAL loaded rows. `rows` is already correctly
  // seeded once by the `useState` initializer above, so skipping the
  // effect's mount run is a no-op in the well-behaved case and removes the
  // race in the buggy one.
  const didMountRef = useRef(false);

  // ONE fetch per mount — `fetchProceduresForDept` is a stable store action
  // reference and is itself single-flight/idempotent per anchor (memory:
  // runtime-safety no-request-storms). Never blocks/crashes on failure.
  useEffect(() => {
    let cancelled = false;
    fetchProceduresForDept(null)
      .then((r) => { if (!cancelled) setCatalogueStatus(r && r.success ? 'loaded' : 'error'); })
      .catch(() => { if (!cancelled) setCatalogueStatus('error'); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-seed ONLY on a genuine external reset (a new reference the parent
  // handed in that is not this component's own last echo) — never on our own
  // round-trip, which would wipe an in-progress blank row.
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (value !== lastEmittedRef.current) {
      setRows((Array.isArray(value) ? value : []).map((r) => toInternalRow(r, makeId)));
      lastEmittedRef.current = value ?? null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const catalogue = useMemo(() => pickerProcedures.filter(isLabCatalogueRow), [pickerProcedures]);

  const commit = (nextRows) => {
    setRows(nextRows);
    const ext = toExternalValue(nextRows);
    lastEmittedRef.current = ext;
    onChange(ext);
  };

  const updateRow = (id, patch) => commit(rows.map((r) => (r._id === id ? { ...r, ...patch } : r)));
  const removeRow = (id) => commit(rows.filter((r) => r._id !== id));
  const addRow = () => {
    if (rows.length >= INVESTIGATION_ROWS_MAX) return;
    commit([...rows, { _id: makeId(), name: '', result: '', source: 'custom', procedure_id: null }]);
  };

  const matchesFor = (query) => {
    const q = query.trim().toLowerCase();
    return q ? catalogue.filter((p) => (p.name || '').toLowerCase().includes(q)) : catalogue;
  };

  const atCap = rows.length >= INVESTIGATION_ROWS_MAX;

  if (disabled) {
    return (
      <div data-testid="investigation-rows-field">
        {label && <label className={labelCls}>{label}</label>}
        {/* verdict-r2.md ruling C-2(d) — the reason-less line removed: the
            panel banner ABOVE this field already gives the real reason
            (locked / no permission); a second, vague line here broke the
            disabled-says-why rule. */}
        {rows.length === 0 ? (
          <div className={readonlyValueCls}>—</div>
        ) : (
          <div className="space-y-1" data-testid="investigation-rows-readonly">
            {rows.map((row, idx) => (
              <div key={row._id} className={readonlyValueCls} data-testid={`investigation-row-readonly-${idx}`}>
                {row.name}{row.result ? ` — ${row.result}` : ''}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="investigation-rows-field">
      {label && <label className={labelCls}>{label}</label>}

      {catalogueStatus === 'error' && (
        <div
          className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 text-[12px] p-2 mb-2"
          data-testid="investigation-rows-catalogue-error"
        >
          Couldn&apos;t load the organisation&apos;s lab catalogue — type the test name to add it.
        </div>
      )}

      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-[11px] text-slate-500 dark:text-slate-400" data-testid="investigation-rows-count">
          {rows.length} of {INVESTIGATION_ROWS_MAX} investigations
        </span>
        <button type="button" className={btnSecondary} onClick={addRow} disabled={atCap} data-testid="investigation-rows-add">
          + Add investigation
        </button>
      </div>
      {atCap && (
        <div className={helperCls} data-testid="investigation-rows-cap-reason">
          Maximum {INVESTIGATION_ROWS_MAX} investigations reached — remove one to add another.
        </div>
      )}
      {/* verdict-r2.md ruling C-3 — a field-level error with NO row (e.g. an
          INVALID_FIELD_SHAPE the server didn't pin to one row) renders once,
          above the rows; a `errorRow`-pinned one renders under that row only
          (below), never both at once. */}
      {error && errorRow == null && (
        <div className={errorCls} data-testid="investigation-rows-error">{error}</div>
      )}

      <div className="space-y-2">
        {rows.map((row, idx) => {
          const isOpen = openRowId === row._id;
          const allMatches = isOpen ? matchesFor(row.name) : [];
          const shownMatches = allMatches.slice(0, CATALOGUE_OPTIONS_CAP);
          const moreCount = allMatches.length - shownMatches.length;
          const showEscape = isOpen && row.name.trim() !== '';
          const showDropdown = isOpen && (shownMatches.length > 0 || showEscape);

          return (
            <div key={row._id} data-testid={`investigation-row-${idx}`}>
              <div className="flex items-start gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  className={inputCls}
                  placeholder="Test name — choose from the list or type your own"
                  maxLength={maxLength}
                  value={row.name}
                  onFocus={() => setOpenRowId(row._id)}
                  onChange={(e) => {
                    setOpenRowId(row._id);
                    updateRow(row._id, { name: e.target.value, source: 'custom', procedure_id: null });
                  }}
                  onKeyDown={(e) => { if (e.key === 'Escape') setOpenRowId(null); }}
                  data-testid={`investigation-row-name-${idx}`}
                />
                {row.source === 'catalogue' && (
                  <div className={helperCls} data-testid={`investigation-row-source-${idx}`}>From your organisation&apos;s lab list</div>
                )}
                {showDropdown && (
                  <div
                    className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg text-sm"
                    data-testid={`investigation-row-options-${idx}`}
                  >
                    {shownMatches.map((p) => (
                      <button
                        type="button"
                        key={p.procedure_id}
                        className="block w-full text-left px-2.5 py-1.5 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700"
                        onClick={() => {
                          updateRow(row._id, { name: p.name, source: 'catalogue', procedure_id: p.procedure_id });
                          setOpenRowId(null);
                        }}
                        data-testid={`investigation-row-option-${idx}-${p.procedure_id}`}
                      >
                        {p.name}
                      </button>
                    ))}
                    {moreCount > 0 && (
                      <div className="px-2.5 py-1.5 text-[11px] text-slate-500 dark:text-slate-400" data-testid={`investigation-row-more-${idx}`}>
                        +{moreCount} more — keep typing
                      </div>
                    )}
                    {showEscape && (
                      <button
                        type="button"
                        className="block w-full text-left px-2.5 py-1.5 border-t border-slate-200 dark:border-slate-700 text-teal-700 dark:text-teal-300 font-medium hover:bg-slate-100 dark:hover:bg-slate-700"
                        onClick={() => {
                          updateRow(row._id, { source: 'custom', procedure_id: null });
                          setOpenRowId(null);
                        }}
                        data-testid={`investigation-row-use-as-custom-${idx}`}
                      >
                        Use as custom: &quot;{row.name.trim()}&quot;
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="flex-1">
                <input
                  type="text"
                  className={inputCls}
                  placeholder="Result"
                  maxLength={INVESTIGATION_RESULT_MAX_LENGTH}
                  value={row.result}
                  onChange={(e) => updateRow(row._id, { result: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); addRow(); }
                  }}
                  data-testid={`investigation-row-result-${idx}`}
                />
              </div>
              <button
                type="button"
                className={btnRemove}
                onClick={() => removeRow(row._id)}
                aria-label="Remove investigation"
                data-testid={`investigation-row-remove-${idx}`}
              >
                ✕
              </button>
            </div>
            {error && errorRow === idx && (
              <div className={errorCls} data-testid={`investigation-row-error-${idx}`}>{error}</div>
            )}
            </div>
          );
        })}
      </div>
      {seededFromPrefill && (
        <div className={helperCls} data-testid="investigation-rows-prefill-hint">{INVESTIGATION_PREFILL_HINT_COPY}</div>
      )}
    </div>
  );
}
